import {
  CaseType,
  NamedOffender,
  ParsedEventsType,
  ReportEventType,
  ReportType,
} from 'src/graphql';
import { getReadableIdWithDash } from 'src/types';
import { uniqueArray } from 'src/utils';
import { DELETION_DATE_ONLY_REGEX } from 'src/utils/commonRegex';
import {
  FormattedNamedOffender,
  NamedOffenderMap,
} from '../formattedNamedOffender.type';
import {
  addExternalNamedOffenders as addNormalizedExternalNamedOffenders,
  addInternalNamedOffenders as addNormalizedInternalNamedOffenders,
} from './getPostsubmissionNamedOffenders';

const addExternalNamedOffenders = (
  event: ParsedEventsType,
  report: ReportType,
  offendersWithReportsAndEvents: Map<string, NamedOffenderMap>,
  eventNamedOffenders: NamedOffender[],
) => {
  event.evidence?.perpetrator
    ?.filter(
      (offender) =>
        offender.value.external &&
        !DELETION_DATE_ONLY_REGEX.test(offender.value.name.toLowerCase()),
    )
    .forEach((offender) => {
      const offenderOriginalId =
        'id' in offender.value ? offender.value.id : offender.id;
      const offenderExistsInExternalNamedOffenders = eventNamedOffenders.find(
        (off) => 'originalId' in off && off.originalId === offenderOriginalId,
      );
      const offenderId =
        offenderExistsInExternalNamedOffenders?.id ?? offenderOriginalId;
      offendersWithReportsAndEvents.set(offenderId, {
        id: offenderId,
        name: offender.value.name,
        reports: [report.readableId],
        events: [getReadableIdWithDash(event.id)],
        external: true,
      });
    });
};

const addInternalNamedOffenders = (
  event: ParsedEventsType,
  report: ReportType,
  offendersWithReportsAndEvents: Map<string, NamedOffenderMap>,
) => {
  event.evidence?.perpetrator
    ?.filter(
      (offender) =>
        !offender.value.external &&
        !offender.value.name.toLowerCase().includes('[deleted'),
    )
    .forEach((offender) => {
      const reportPerpetrators = report.Perpetrators?.find(
        (p) => p.id === Number(offender.value.id),
      );
      const offenderId = String(offender.value.id);
      const existing = offendersWithReportsAndEvents.get(offenderId);
      offendersWithReportsAndEvents.set(offenderId, {
        id: offenderId,
        name: offender.value.name ?? '',
        site: reportPerpetrators?.site ?? '',
        department: reportPerpetrators?.department ?? '',
        country: reportPerpetrators?.country ?? '',
        reports: uniqueArray([
          ...(existing ? existing.reports : []),
          report.readableId,
        ]),
        events: uniqueArray([
          ...(existing ? existing.events : []),
          getReadableIdWithDash(event.id),
        ]),
        external: false,
      });
    });
};

export const getEventPresubmissionNamedOffenders = (
  event: ParsedEventsType,
  report: ReportType,
  useNormalizedData: boolean,
  reportEvent?: ReportEventType,
) => {
  const offendersWithReportsAndEvents = new Map<string, NamedOffenderMap>();
  const eventNamedOffenders =
    reportEvent?.NamedOffenders.filter(
      (offender) => offender.event === event.id && !offender.postSubmission,
    ) ?? [];

  if (useNormalizedData) {
    addNormalizedExternalNamedOffenders(
      eventNamedOffenders,
      offendersWithReportsAndEvents,
    );
    addNormalizedInternalNamedOffenders(
      eventNamedOffenders,
      offendersWithReportsAndEvents,
    );
  } else {
    addInternalNamedOffenders(event, report, offendersWithReportsAndEvents);
    addExternalNamedOffenders(
      event,
      report,
      offendersWithReportsAndEvents,
      eventNamedOffenders,
    );
  }

  const namedOffenders: FormattedNamedOffender[] = [];
  offendersWithReportsAndEvents.forEach((value, key) =>
    namedOffenders.push({
      id: key,
      reports: value.reports.map((id) => ({ id, type: 'Presubmission' })),
      events: value.events.map((id) => ({ id, type: 'Presubmission' })),
      name: value.name,
      external: value.external,
      department: value.department,
      site: value.site,
      country: value.country,
    }),
  );

  return namedOffenders;
};

export const getReportPresubmissionNamedOffenders = (
  report: ReportType,
  useNormalizedData: boolean,
) => {
  const offendersWithReportsAndEvents = new Map<string, NamedOffenderMap>();
  const reportEvents = report.ReportEvents ?? [];
  const eventNamedOffenders = reportEvents.flatMap((event) =>
    event.NamedOffenders.filter(
      (offender) => offender.event === event.id && !offender.postSubmission,
    ),
  );

  if (useNormalizedData) {
    addNormalizedExternalNamedOffenders(
      eventNamedOffenders,
      offendersWithReportsAndEvents,
    );
    addNormalizedInternalNamedOffenders(
      eventNamedOffenders,
      offendersWithReportsAndEvents,
    );
  } else {
    const reportEvidenceEvents = report.Evidence?.Events ?? [];
    reportEvidenceEvents?.forEach((event) => {
      addInternalNamedOffenders(event, report, offendersWithReportsAndEvents);
      addExternalNamedOffenders(
        event,
        report,
        offendersWithReportsAndEvents,
        eventNamedOffenders,
      );
    });
  }

  const namedOffenders: FormattedNamedOffender[] = [];
  offendersWithReportsAndEvents.forEach((value, key) =>
    namedOffenders.push({
      id: key,
      reports: value.reports.map((id) => ({ id, type: 'Presubmission' })),
      events: value.events.map((id) => ({ id, type: 'Presubmission' })),
      name: value.name,
      external: value.external,
      department: value.department,
      site: value.site,
      country: value.country,
    }),
  );

  return namedOffenders;
};

export const getCasePresubmissionNamedOffenders = (
  caseInstance: CaseType,
  useNormalizedData: boolean,
) => {
  const offendersWithReportsAndEvents = new Map<string, NamedOffenderMap>();
  const reports = caseInstance.Reports ?? [];
  const reportEvents =
    reports?.flatMap((report) => report.ReportEvents ?? []) ?? [];
  const eventNamedOffenders = reportEvents.flatMap((event) =>
    event.NamedOffenders.filter(
      (offender) => offender.event === event.id && !offender.postSubmission,
    ),
  );

  if (useNormalizedData) {
    addNormalizedExternalNamedOffenders(
      eventNamedOffenders,
      offendersWithReportsAndEvents,
    );
    addNormalizedInternalNamedOffenders(
      eventNamedOffenders,
      offendersWithReportsAndEvents,
    );
  } else {
    reports?.forEach((report) => {
      report.Evidence?.Events?.forEach((event) => {
        addInternalNamedOffenders(event, report, offendersWithReportsAndEvents);
        addExternalNamedOffenders(
          event,
          report,
          offendersWithReportsAndEvents,
          eventNamedOffenders,
        );
      });
    });
  }

  const namedOffenders: FormattedNamedOffender[] = [];
  offendersWithReportsAndEvents.forEach((value) =>
    namedOffenders.push({
      id: value.id,
      reports: value.reports.map((id) => ({ id, type: 'Presubmission' })),
      events: value.events.map((id) => ({ id, type: 'Presubmission' })),
      name: value.name,
      external: value.external,
      department: value.department,
      site: value.site,
      country: value.country,
    }),
  );

  return namedOffenders;
};
